import { Store } from 'vuex';
import { AxiosInstance, AxiosError } from 'axios';

import { AppState } from '@/store';
import { ApiService } from '@/services/api/api.service';
import { Logger } from '@/services/logger/logger';
import { CustomerDeliveryAddress, DeliveryAddressValidationRequest, DeliveryAddressValidationResponse, DeliveryApiException, DeliveryApiValidationError } from '@/models';
import { AnalyticsService } from '../analytics/analytics.service';

export interface FindAChipotleService {
  setSelectedDeliveryAddress(details: google.maps.places.PlaceResult): CustomerDeliveryAddress;
  createDeliveryAddressValidationRequest(details: google.maps.places.PlaceResult): DeliveryAddressValidationRequest;
  customerDeliveryAddressToValidationRequest(deliveryAddress: CustomerDeliveryAddress): DeliveryAddressValidationRequest;
}

export class AppFindAChipotleService extends ApiService implements FindAChipotleService {
  constructor(store: Store<AppState>, httpClient: AxiosInstance, logger: Logger, protected analytics: AnalyticsService) {
    super(store, httpClient, logger, analytics);
  }

  // transforms
  setSelectedDeliveryAddress(details: google.maps.places.PlaceResult): CustomerDeliveryAddress {
    const ext = this.extractPlaceTerm;
    return {
      streetAddress1: (ext(details, 'street_number') + ' ' + ext(details, 'route')).trim(),
      city: ext(details, 'locality') || ext(details, 'sublocality'),
      state: ext(details, 'administrative_area_level_1'),
      postalCode: ext(details, 'postal_code'),
      countryCode: ext(details, 'country'),
      latitude: details.geometry!.location.lat(),
      longitude: details.geometry!.location.lng(),
    };
  }

  createDeliveryAddressValidationRequest(details: google.maps.places.PlaceResult): DeliveryAddressValidationRequest {
    const ext = this.extractPlaceTerm;
    const deliveryAddress: DeliveryAddressValidationRequest = {
      dropOffAddress: {
        addressLine1: (ext(details, 'street_number') + ' ' + ext(details, 'route')).trim(),
        locality: ext(details, 'locality') || ext(details, 'sublocality'),
        administrativeArea: ext(details, 'administrative_area_level_1'),
        postalCode: ext(details, 'postal_code'),
        countryCode: ext(details, 'country'),
      },

      // Required fields for delivery address validation
      orderValue: 0,
      entreeCount: 0,
    };
    return deliveryAddress;
  }

  extractPlaceTerm(details: google.maps.places.PlaceResult, term: string) {
    const component = details.address_components!.find(item => item.types.some(type => type === term));
    if (component) {
      return component.short_name;
    }
    return '';
  }

  customerDeliveryAddressToValidationRequest(deliveryAddress: CustomerDeliveryAddress): DeliveryAddressValidationRequest {
    const validationRequest: DeliveryAddressValidationRequest = {
      dropOffAddress: {
        addressLine1: `${deliveryAddress.streetAddress1} ${deliveryAddress.streetAddress2 || ''}`.trim(),
        locality: deliveryAddress.city,
        administrativeArea: deliveryAddress.state,
        postalCode: deliveryAddress.postalCode,
        countryCode: deliveryAddress?.countryCode ?? '',
      },
      // Required fields for delivery address validation
      orderValue: 0,
      entreeCount: 0,
    };
    return validationRequest;
  }
}
